import { GetStaticPaths, GetStaticPropsContext, InferGetStaticPropsType } from 'next';
import { ReactElement } from 'react';
import { useStoryblok } from 'storyblok';

import { Layout } from '@/components';
import { TagsProvider } from '@/components/cms/Tags/TagsContext';
import Footer from '@/components/global/Footer';
import Header from '@/components/global/Header';
import BaseSeo from '@/components/seo/BaseSeo';
import { VERSION } from '@/lib/constants';
import { getStoryBlokProps } from '@/lib/storyblok/storyblokGetStaticProps';
import { getStoryBlokNavLinks } from '@/lib/storyblok/storyblokNavLinks';

import DynamicComponent from '../../storyblok/components/DynamicComponent';

const Page = ({
  story,
  preview,
  resolve_relations
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const sbStory = preview ? useStoryblok(story, preview, resolve_relations) : story;
  const { name = '' } = sbStory;
  const globalLinks = getStoryBlokNavLinks(story);

  const {
    content: { headerTheme = '', SEO = {}, footerTheme = '' },
    full_slug
  } = sbStory;

  return (
    <div>
      <BaseSeo
        title={SEO?.title}
        description={SEO?.description}
        ogDescription={SEO?.og_description}
        ogTitle={SEO?.og_title}
        image={SEO?.og_image}
        path={full_slug}
      />
      <Header
        navigation={globalLinks}
        headerTheme={headerTheme}
        pageName="Articles & Guides"
        renderParentOnly
        blok={sbStory.content}
      />
      <main id="main">
        <TagsProvider tags={sbStory?.tag_list}>
          {sbStory && sbStory.content && <DynamicComponent blok={sbStory.content} />}
        </TagsProvider>
      </main>
      <Footer theme="grey-light" navigation={globalLinks.footerLinks} />
    </div>
  );
};

export default Page;

export interface pathParams {
  channel: string;
  locale: string;
  slug: string;
}

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking'
  };
};

export const getStaticProps = async (context: GetStaticPropsContext) => {
  const route = 'articles-guides/'; // trailing "/" please
  const pageSlug = route + context.params?.slug?.toString()!;
  const preview = context.preview || false;
  const resolveRelations = 'page.globalHeader,page.globalFooter,GlobalBlock.content';
  const story = await getStoryBlokProps({ pageSlug, context, VERSION, resolveRelations });

  if (story?.notFound) {
    return story;
  }

  return {
    props: {
      preview,
      story: story || null,
      resolve_relations: resolveRelations
    },
    revalidate: 60 * 60 // value in seconds, how often ISR will trigger on the server
  };
};

Page.getLayout = function getLayout(page: ReactElement) {
  return <Layout>{page}</Layout>;
};
